import React, { useState } from 'react';

/**
 * Export avancé avec options
 */
const AdvancedExport = ({ documents, selectedDocuments, visibleColumns, onClose }) => {
  const [format, setFormat] = useState('csv');
  const [scope, setScope] = useState(selectedDocuments.length > 0 ? 'selected' : 'all');
  const [includeHeaders, setIncludeHeaders] = useState(true);
  const [columnsToExport, setColumnsToExport] = useState('visible');

  const handleExport = () => {
    const docsToExport = scope === 'selected' ? selectedDocuments : documents;
    
    if (docsToExport.length === 0) {
      alert('Aucun document à exporter');
      return;
    }

    if (format === 'csv') {
      exportToCSV(docsToExport);
    } else if (format === 'json') {
      exportToJSON(docsToExport);
    }
  };

  const exportToCSV = (docs) => {
    // Définir les colonnes
    const allColumns = [
      { key: 'reference', label: 'Référence' },
      { key: 'type', label: 'Type' },
      { key: 'client', label: 'Client' },
      { key: 'statut', label: 'Statut Document' },
      { key: 'date_creation', label: 'Date Création' },
      { key: 'date_derniere_mise_a_jour', label: 'Dernière MAJ' },
      { key: 'total_ttc', label: 'Total TTC' },
      { key: 'montant_paye', label: 'Montant Payé' },
      { key: 'reste_a_payer', label: 'Reste à Payer' },
      { key: 'transporteur_name', label: 'Transporteur' },
      { key: 'opportunite_id', label: 'Opportunité ID' },
      { key: 'operation_id', label: 'Opération ID' }
    ];

    // Header
    let csvContent = '';
    if (includeHeaders) {
      csvContent += allColumns.map(col => `"${col.label}"`).join(',') + '\n';
    }

    // Rows
    docs.forEach(doc => {
      const row = allColumns.map(col => {
        let value = doc[col.key];
        
        // Formatage spécial
        if (col.key === 'client') {
          value = doc.client?.name || '';
        } else if (col.key === 'date_creation' || col.key === 'date_derniere_mise_a_jour') {
          value = value ? new Date(value).toLocaleDateString('fr-FR') : '';
        } else if (typeof value === 'number') {
          value = value.toLocaleString('fr-FR');
        } else if (value === null || value === undefined) {
          value = '';
        }
        
        // Échapper les guillemets
        return `"${String(value).replace(/"/g, '""')}"`;
      });
      
      csvContent += row.join(',') + '\n';
    });

    // Télécharger
    const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `documents_export_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    URL.revokeObjectURL(url);

    alert(`${docs.length} document(s) exporté(s) en CSV`);
    onClose();
  };

  const exportToJSON = (docs) => {
    const jsonContent = JSON.stringify(docs, null, 2);
    const blob = new Blob([jsonContent], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `documents_export_${new Date().toISOString().split('T')[0]}.json`;
    link.click();
    URL.revokeObjectURL(url);

    alert(`${docs.length} document(s) exporté(s) en JSON`);
    onClose();
  };

  const docsToExport = scope === 'selected' ? selectedDocuments : documents;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-2xl w-full max-w-lg">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white px-6 py-4 flex items-center justify-between rounded-t-lg">
          <div className="flex items-center gap-3">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <h3 className="text-lg font-semibold">Export Avancé</h3>
          </div>
          <button 
            onClick={onClose}
            className="p-1 hover:bg-blue-500 rounded-full transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Body */}
        <div className="p-6 space-y-6">
          {/* Format */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-3">
              Format d'export
            </label>
            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={() => setFormat('csv')}
                className={`p-4 rounded-lg border-2 transition-all ${
                  format === 'csv'
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className="text-3xl mb-2">📊</div>
                <div className="font-medium text-gray-900">CSV</div>
                <div className="text-xs text-gray-500 mt-1">Excel compatible</div>
              </button>
              
              <button
                onClick={() => setFormat('json')}
                className={`p-4 rounded-lg border-2 transition-all ${
                  format === 'json'
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className="text-3xl mb-2">💾</div>
                <div className="font-medium text-gray-900">JSON</div>
                <div className="text-xs text-gray-500 mt-1">Pour développeurs</div>
              </button>
            </div>
          </div>

          {/* Scope */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-3">
              Documents à exporter
            </label>
            <div className="space-y-2">
              <label className="flex items-center p-3 rounded-lg border-2 cursor-pointer transition-all hover:border-gray-300">
                <input
                  type="radio"
                  name="scope"
                  value="all"
                  checked={scope === 'all'}
                  onChange={(e) => setScope(e.target.value)}
                  className="w-4 h-4 text-blue-600"
                />
                <span className="ml-3 text-gray-900">
                  Tous les documents visibles <span className="text-gray-500">({documents.length})</span>
                </span>
              </label>
              
              {selectedDocuments.length > 0 && (
                <label className="flex items-center p-3 rounded-lg border-2 cursor-pointer transition-all hover:border-gray-300">
                  <input
                    type="radio"
                    name="scope"
                    value="selected"
                    checked={scope === 'selected'}
                    onChange={(e) => setScope(e.target.value)}
                    className="w-4 h-4 text-blue-600"
                  />
                  <span className="ml-3 text-gray-900">
                    Documents sélectionnés <span className="text-blue-600 font-medium">({selectedDocuments.length})</span>
                  </span>
                </label>
              )}
            </div>
          </div>

          {/* Options */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-3">
              Options
            </label>
            <div className="space-y-2">
              <label className="flex items-center p-3 rounded-lg bg-gray-50 cursor-pointer">
                <input
                  type="checkbox"
                  checked={includeHeaders}
                  onChange={(e) => setIncludeHeaders(e.target.checked)}
                  className="w-4 h-4 text-blue-600 rounded"
                />
                <span className="ml-3 text-gray-900">Inclure les en-têtes de colonnes</span>
              </label>
            </div>
          </div>

          {/* Summary */}
          <div className="bg-blue-50 border border-blue-100 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <svg className="w-5 h-5 text-blue-600 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div className="text-sm text-blue-800">
                <p className="font-medium mb-1">Résumé de l'export:</p>
                <ul className="space-y-1 text-blue-700">
                  <li>• Format: <span className="font-medium">{format.toUpperCase()}</span></li>
                  <li>• Nombre de documents: <span className="font-medium">{docsToExport.length}</span></li>
                  <li>• En-têtes: <span className="font-medium">{includeHeaders ? 'Oui' : 'Non'}</span></li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex items-center justify-between rounded-b-lg">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 hover:bg-gray-200 rounded-lg transition-colors"
          >
            Annuler
          </button>
          <button
            onClick={handleExport}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium flex items-center gap-2"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
            </svg>
            Exporter
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdvancedExport;
